import axios from "axios";

/**
 * Token AAD frais AU MOMENT DE LA REQUÊTE.
 *
 * Le portail acquérait son token une fois au montage (`InitComponent`) et le passait ensuite en
 * prop à tous les services. Un token AAD vit environ une heure : au-delà, la page reste vivante
 * — les lectures faites au chargement ont réussi — mais toute écriture repart avec un token
 * périmé et se fait refuser en 401. Cas observé en prod : token émis à 13:35 UTC, expiré à
 * 14:47:45, sauvegarde d'un widget refusée à 14:50.
 *
 * Rafraîchir sur minuterie ne fait que réduire la fenêtre : entre deux tics, ou après une mise en
 * veille de la machine, la requête part quand même avec un token mort. Le seul instant où la
 * fraîcheur est vérifiable est celui de la requête — d'où l'intercepteur ci-dessous.
 *
 * Coût : nul dans le cas courant. `acquireTokenSilent` sert le cache MSAL tant que le token est
 * valide ; on ne l'appelle même pas tant que l'expiration est à plus de 2 minutes, et les appels
 * concurrents partagent la même promesse (une rafale de 30 requêtes au chargement d'un onglet ne
 * déclenche donc qu'un seul renouvellement).
 */

let msalInstance = null;
let cached = { token: null, expiresAt: 0 };
let inflight = null;

/** Marge avant expiration : en deçà, on renouvelle. Couvre la latence réseau d'une requête lente. */
const REFRESH_MARGIN_MS = 2 * 60 * 1000;

/** À appeler une fois, au démarrage (index.js), avec l'instance MSAL de l'application. */
export const registerMsalInstance = (instance) => {
    msalInstance = instance;
};

/** Expiration réelle du JWT, pour ne pas se fier à une durée supposée. 0 si illisible. */
const expiryOf = (jwt) => {
    try {
        const payload = JSON.parse(atob(String(jwt).split('.')[1]));
        return payload?.exp ? payload.exp * 1000 : 0;
    } catch {
        return 0;
    }
};

const acquire = async () => {
    if (!msalInstance) return null;
    // `getActiveAccount` peut être null si l'app ne l'a jamais posé : on retombe sur le premier
    // compte connu, qui est celui qu'utilise le reste du portail (`accounts[0]`).
    const account = msalInstance.getActiveAccount?.() || msalInstance.getAllAccounts?.()[0];
    if (!account) return null;

    try {
        // Mêmes paramètres que l'acquisition historique (Services/GraphServices.getAccessToken) :
        // pas de `scopes` explicite, donc même audience qu'aujourd'hui. Ce module change QUAND
        // le token est obtenu, jamais LEQUEL.
        const response = await msalInstance.acquireTokenSilent({ account });
        const token = response?.accessToken || null;
        if (token) cached = { token, expiresAt: expiryOf(token) };
        return token;
    } catch (error) {
        // Silencieux côté UI : la requête part avec le token qu'elle avait. Un 401 est alors
        // remonté et interprété par l'appelant (« session expirée »), ce qui vaut mieux qu'une
        // requête SANS en-tête Authorization — l'ancien comportement, qui produisait des 400
        // inexplicables.
        console.warn('⚠️ acquireTokenSilent failed, keeping the current token:', error?.errorCode || error?.message);
        return null;
    }
};

/** Token valide, depuis le cache si possible. `null` si MSAL ne peut rien fournir. */
export const getFreshToken = async () => {
    if (cached.token && cached.expiresAt - Date.now() > REFRESH_MARGIN_MS) {
        return cached.token;
    }
    if (!inflight) {
        inflight = acquire().finally(() => { inflight = null; });
    }
    return inflight;
};

/**
 * Plafond d'attente. Si MSAL ne répond pas (réseau coupé, iframe bloquée), la requête part avec
 * l'en-tête qu'elle avait plutôt que de rester suspendue : un intercepteur ne doit JAMAIS pouvoir
 * bloquer indéfiniment toutes les requêtes de l'application.
 */
const ACQUIRE_TIMEOUT_MS = 4000;

const withTimeout = (promise) => Promise.race([
    promise,
    new Promise((resolve) => setTimeout(() => resolve(null), ACQUIRE_TIMEOUT_MS))
]);

/**
 * Corps de l'intercepteur — exporté pour être testable sans monter axios.
 *
 * Ne touche QUE les requêtes portant déjà un `Bearer`. C'est délibéré : on n'ajoute pas
 * d'Authorization à une requête qui n'en a pas — typiquement un GET sur une URL SAS de blob, où
 * l'en-tête ferait échouer la lecture et enverrait le token à un tiers. Une requête portant un
 * autre schéma (Basic, SAS…) est laissée intacte pour la même raison.
 *
 * En cas d'échec ou de dépassement du délai, la config est renvoyée INCHANGÉE : au pire on
 * retombe sur le comportement d'avant ce module.
 */
export const attachFreshToken = async (config) => {
    try {
        const header = config?.headers?.Authorization || config?.headers?.authorization;
        if (!header || !String(header).startsWith('Bearer ')) return config;

        const fresh = await withTimeout(getFreshToken());
        if (fresh) config.headers.Authorization = `Bearer ${fresh}`;
        return config;
    } catch (error) {
        // Une exception ici ferait échouer la requête AVANT même le réseau : jamais.
        console.warn('⚠️ token interceptor skipped:', error?.message);
        return config;
    }
};

axios.interceptors.request.use(attachFreshToken);

export default { registerMsalInstance, getFreshToken, attachFreshToken };
